import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import {
  BodyText,
  Button,
  IllusEmployeeBag,
  Image,
  theme,
  VStack,
} from '@sweetgreen/garnish';

import {
  AccountOrdersLoading,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  OrderCard,
} from '@order/components';
import type { Order } from '@order/graphql';
import { useLineItemsNames } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { useOrderStatusNavigation } from '../OrderStatusScreen/OrderStatus.navigation';
import { useOrderStatusTelemetry } from '../OrderStatusScreen/OrderStatus.telemetry';
import { usePaginatedOrders } from './hooks';

export const OrdersScreen = () => {
  const { t } = useLocalizationContext();
  const {
    orders,
    canLoadMore,
    isLoading,
    refetchOrderHistory,
    handleLoadMore,
  } = usePaginatedOrders();

  if (isLoading && orders.length === 0) return <AccountOrdersLoading />;

  return (
    <AccountScreenContainer screen="orders">
      <AccountScreenTitle title={t('account.orders.title')} />
      <AccountScreenSection>
        <OrdersScreenContent
          orders={orders}
          canLoadMore={canLoadMore}
          isLoading={isLoading}
          refetchOrderHistory={refetchOrderHistory}
          handleLoadMore={handleLoadMore}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

export const OrdersScreenContent = (props: OrdersScreenContentProps) => {
  const {
    orders,
    canLoadMore,
    isLoading,
    refetchOrderHistory,
    handleLoadMore,
  } = props;

  const { t } = useLocalizationContext();

  if (!isLoading && orders.length === 0) return <EmptyState />;

  return (
    <>
      <VStack accessibilityLabel={t('account.orders.list')}>
        {orders.map((order) => (
          <ConnectedOrderCard
            key={order.id}
            order={order}
            refetchOrderHistory={refetchOrderHistory}
          />
        ))}
      </VStack>

      {orders.length > 0 && canLoadMore ? (
        <Button
          palette="secondary"
          testID="account.orders.load-more"
          accessibilityRole="button"
          accessibilityLabel={t('account.orders.load-more')}
          accessibilityHint={t('account.orders.load-more')}
          style={styles.button}
          isLoading={isLoading}
          onPress={handleLoadMore}
        >
          {t('account.orders.load-more')}
        </Button>
      ) : null}
    </>
  );
};

const ConnectedOrderCard = (props: ConnectedOrderCardProps) => {
  const { order, refetchOrderHistory } = props;

  const orderLineItemsNames = useLineItemsNames(order?.lineItems);
  const isOrderCanceled = Boolean(order?.isCanceled);

  const { navigateToOrderStatusScreen } = useOrderStatusNavigation();
  const { handleOrderStatusEvent } = useOrderStatusTelemetry();

  const handlePress = useCallback(() => {
    navigateToOrderStatusScreen(order.id);
    handleOrderStatusEvent('account.orders');
  }, [order.id, navigateToOrderStatusScreen, handleOrderStatusEvent]);

  return (
    <OrderCard
      orderId={order.id}
      orderType={convertOrderType(order)}
      wantedTime={order.wantedTime}
      restaurantId={order.restaurant.id}
      restaurantName={order.restaurant.name}
      addressName={order.deliveryOrderDetail?.address?.name ?? undefined}
      description={orderLineItemsNames}
      total={order.total ?? 0}
      assetUrl={order.asset?.url ?? ''}
      isCancellable={order.canCancel}
      isCanceled={isOrderCanceled}
      onPress={handlePress}
      refetchOrderHistory={refetchOrderHistory}
    />
  );
};

const convertOrderType = (order: Order): AddressType => {
  if (order.restaurant.isOutpost) return 'outpost';

  return order.orderType.toLowerCase() as AddressType;
};

const EmptyState = () => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.emptyContainer}>
      <Image
        source={IllusEmployeeBag}
        style={styles.emptyImage}
        accessibilityIgnoresInvertColors
      />
      <BodyText style={styles.noResultsText}>
        {t('account.orders.empty')}
      </BodyText>
    </View>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type OrdersScreenContentProps = ReturnType<typeof usePaginatedOrders>;
type ConnectedOrderCardProps = Readonly<{
  order: Order;
  refetchOrderHistory: () => void;
}>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyImage: {
    width: 220,
    height: 220,
    marginBottom: theme.spacing['4'],
  },
  noResultsText: {
    textAlign: 'center',
  },
  button: {
    minWidth: 200,
    maxWidth: 200,
    marginHorizontal: 'auto',
    marginTop: theme.spacing['6'],
  },
});
